import { LocalAtRecord } from '../../db';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { Feature, Geometry } from 'geojson';
import { parseGeoJsonText } from '../../utilities/geojson-utils';

/**
 * Domain entity representing a job in the Boundary Approval workflow
 */
export class BoundaryApprovalJob {
  public readonly id: string;
  public readonly name: string;
  public readonly jobRecord: LocalAtRecord<JobsFieldSet>;
  public readonly operator: string;
  public readonly sampleDate: string;
  public readonly boundaryChangeNotes: string;

  private _isVisible: boolean = true;
  private _isActive: boolean = false;
  private _hasUnsavedEdits: boolean = false;
  private _isApproved: boolean = false;

  constructor(jobRecord: LocalAtRecord<JobsFieldSet>) {
    this.id = jobRecord.id;
    this.name = jobRecord.get('Name') || jobRecord.id;
    this.jobRecord = jobRecord;

    // Operator Name is an array, get first element
    const operatorName = jobRecord.get('Operator Name');
    this.operator = Array.isArray(operatorName) && operatorName.length > 0 ? operatorName[0] : 'Unknown Operator';

    this.sampleDate = jobRecord.get('Sample Date') || 'Unknown Sample Date';
    this.boundaryChangeNotes = (jobRecord.get('Boundary Change Notes') as string | undefined) || '';

    // Check if already approved
    this._isApproved = jobRecord.get('Bdy Approved for Send?') === true;
  }

  get isVisible(): boolean {
    return this._isVisible;
  }

  setVisible(visible: boolean): void {
    this._isVisible = visible;
  }

  get isActive(): boolean {
    return this._isActive;
  }

  setActive(active: boolean): void {
    this._isActive = active;
  }

  get hasUnsavedEdits(): boolean {
    return this._hasUnsavedEdits;
  }

  setHasUnsavedEdits(hasEdits: boolean): void {
    this._hasUnsavedEdits = hasEdits;
  }

  get isApproved(): boolean {
    return this._isApproved;
  }

  markAsApproved(): void {
    this._isApproved = true;
    this._hasUnsavedEdits = false;
  }

  /**
   * Determines if this job can be activated
   * @returns true if the job can be activated (not already approved)
   */
  canBeActivated(): boolean {
    return !this._isApproved;
  }

  /**
   * Resets the job state
   */
  resetEdits(): void {
    this._hasUnsavedEdits = false;
  }

  /**
   * Gets the job's boundaries for map centering
   * Extracts bounds from the GeoJSON text field
   */
  getBounds(): [[number, number], [number, number], [number, number], [number, number]] | undefined {
    const geoJsonText = this.jobRecord.get('Bnd GeoJSON Text');

    if (!geoJsonText || typeof geoJsonText !== 'string') {
      return undefined;
    }

    try {
      const geoJson = parseGeoJsonText(geoJsonText);

      if (!geoJson) {
        return undefined;
      }

      // Extract all coordinates from all features to find the bounding box
      const allCoords: [number, number][] = [];

      geoJson.features.forEach((feature: Feature<Geometry>) => {
        const geometry = feature.geometry;

        if (!geometry) {
          return;
        }

        if (geometry.type === 'Polygon') {
          // Polygon coordinates are [ring][point][lon, lat]
          geometry.coordinates.forEach((ring: number[][]) => {
            ring.forEach((coord: number[]) => {
              allCoords.push([coord[0], coord[1]]);
            });
          });
        } else if (geometry.type === 'MultiPolygon') {
          // MultiPolygon coordinates are [polygon][ring][point][lon, lat]
          geometry.coordinates.forEach((polygon: number[][][]) => {
            polygon.forEach((ring: number[][]) => {
              ring.forEach((coord: number[]) => {
                allCoords.push([coord[0], coord[1]]);
              });
            });
          });
        }
      });

      if (allCoords.length === 0) {
        return undefined;
      }

      // Find min/max coordinates to create bounding box
      const lons = allCoords.map((c) => c[0]);
      const lats = allCoords.map((c) => c[1]);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);

      // Return as four corners: [SW, SE, NE, NW]
      return [
        [minLon, minLat], // Southwest
        [maxLon, minLat], // Southeast
        [maxLon, maxLat], // Northeast
        [minLon, maxLat], // Northwest
      ];
    } catch (e) {
      console.error(`Failed to parse Bnd GeoJSON Text for job ${this.id}:`, e);

      return undefined;
    }
  }
}
